/**
 * RadiationDosePanel Component - Afișare doză de radiație (RDSR)
 * 
 * Afișează datele de doză extrase din RDSR (Radiation Dose Structured Report):
 * - Pentru Radiografie/Fluoroscopie: DAP (Dose Area Product) per expunere + total
 * - Pentru CT: DLP (Dose Length Product) + CTDIvol per achiziție + total
 * 
 * Se afișează doar dacă studiul conține RDSR.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Chip,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  RadioButtonChecked as RadiationIcon,
} from '@mui/icons-material';
import { medicApi } from '../../services/api';

interface IrradiationEvent {
  event_number: number;
  description: string | null;
  dap: number | null;
  dap_unit: string;
  air_kerma: number | null;
  dlp: number | null;
  dlp_unit: string;
  ctdi_vol: number | null;
  kvp: number | null;
  mas: number | null;
  exposure_time: number | null;
}

interface RadiationDoseData {
  has_rdsr: boolean;
  modality_type: string | null;
  total_dap: number | null;
  total_dap_unit: string;
  total_air_kerma: number | null;
  total_dlp: number | null;
  total_dlp_unit: string;
  events: IrradiationEvent[];
  events_count: number;
  rdsr_series_uid: string | null;
  rdsr_instance_uid: string | null;
  device_name: string | null;
  manufacturer: string | null;
}

interface RadiationDosePanelProps {
  studyId: string;
  expanded?: boolean;  // Dacă se afișează automat expandat
}

// Formatează numărul cu unități
const formatDose = (value: number | null, unit: string, decimals: number = 2): string => {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(decimals)} ${unit}`;
};

const RadiationDosePanel: React.FC<RadiationDosePanelProps> = ({
  studyId,
  expanded: initialExpanded = false,
}) => {
  const [doseData, setDoseData] = useState<RadiationDoseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(initialExpanded);

  useEffect(() => {
    const fetchRadiationDose = async () => {
      if (!studyId) {
        console.log('[RadiationDosePanel] No studyId provided');
        return;
      }
      
      console.log('[RadiationDosePanel] Fetching radiation dose for study:', studyId);
      setLoading(true);
      setError(null);
      
      try {
        const data = await medicApi.getRadiationDose(studyId);
        console.log('[RadiationDosePanel] Response:', data);
        setDoseData(data);
      } catch (err: any) {
        console.error('[RadiationDosePanel] Error fetching radiation dose:', err);
        setError(err.response?.data?.detail || 'Eroare la încărcarea dozei de radiație');
        setDoseData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRadiationDose();
  }, [studyId]);

  // Nu afișăm nimic dacă încă se încarcă sau nu există RDSR
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'background.paper' }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Se verifică RDSR...
        </Typography>
      </Box>
    );
  }

  // Nu afișăm nimic dacă nu există RDSR sau a fost eroare
  if (error || !doseData || !doseData.has_rdsr) {
    return null;
  }

  const isCT = doseData.modality_type === 'CT';
  const hasEvents = doseData.events && doseData.events.length > 0;

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        bgcolor: 'background.paper', 
        borderTop: '1px solid',
        borderColor: 'divider',
        mt: 0,
      }}
    >
      {/* Header - Sumar doză */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          cursor: hasEvents ? 'pointer' : 'default',
          '&:hover': hasEvents ? { bgcolor: 'action.hover' } : {},
        }}
        onClick={() => hasEvents && setShowDetails(!showDetails)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RadiationIcon 
            sx={{ 
              color: 'warning.main', 
              fontSize: '1.2rem',
            }} 
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
            ☢️ Doză Radiație
          </Typography>
          
          {doseData.events_count > 0 && (
            <Chip
              label={`${doseData.events_count} ${isCT ? 'achiziții' : 'expuneri'}`}
              size="small"
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                bgcolor: 'warning.lighter',
                color: 'warning.dark',
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Total DAP pentru radiografie */}
          {!isCT && doseData.total_dap !== null && (
            <Tooltip title="Dose Area Product Total">
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                DAP: {formatDose(doseData.total_dap, doseData.total_dap_unit)}
              </Typography>
            </Tooltip>
          )}

          {/* Total DLP pentru CT */}
          {isCT && doseData.total_dlp !== null && (
            <Tooltip title="Dose Length Product Total">
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                DLP: {formatDose(doseData.total_dlp, doseData.total_dlp_unit)}
              </Typography>
            </Tooltip>
          )}

          {/* Expand/collapse button */}
          {hasEvents && (
            <IconButton size="small" sx={{ p: 0.5 }}>
              {showDetails ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Detalii per eveniment */}
      <Collapse in={showDetails} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {/* Info echipament */}
          {(doseData.manufacturer || doseData.device_name) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {doseData.manufacturer && `${doseData.manufacturer}`}
              {doseData.manufacturer && doseData.device_name && ' - '}
              {doseData.device_name && `${doseData.device_name}`}
            </Typography>
          )}

          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Descriere</TableCell>
                  {isCT ? (
                    <>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>CTDIvol</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>DLP</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>DAP</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>kVp</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>mAs</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {doseData.events.map((event) => (
                  <TableRow key={event.event_number} hover>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5, color: 'text.secondary' }}>
                      {event.event_number}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {event.description || '-'}
                    </TableCell>
                    {isCT ? (
                      <>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5, fontFamily: 'monospace' }}>
                          {event.ctdi_vol !== null ? `${event.ctdi_vol.toFixed(1)} mGy` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5, fontFamily: 'monospace' }}>
                          {formatDose(event.dlp, event.dlp_unit, 1)}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5, fontFamily: 'monospace' }}>
                          {formatDose(event.dap, event.dap_unit)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5, fontFamily: 'monospace' }}>
                          {event.kvp !== null ? `${event.kvp.toFixed(0)}` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5, fontFamily: 'monospace' }}>
                          {event.mas !== null ? `${event.mas.toFixed(1)}` : '-'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}

                {/* Rând total */}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.75 }} colSpan={2}>
                    TOTAL
                  </TableCell>
                  {isCT ? (
                    <>
                      <TableCell sx={{ py: 0.75 }}></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75, fontFamily: 'monospace', color: 'warning.dark' }}>
                        {formatDose(doseData.total_dlp, doseData.total_dlp_unit, 1)}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.75, fontFamily: 'monospace', color: 'warning.dark' }}>
                        {formatDose(doseData.total_dap, doseData.total_dap_unit)}
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}></TableCell>
                      <TableCell sx={{ py: 0.75 }}></TableCell>
                    </>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default RadiationDosePanel;
